import type { ComponentType } from 'react';

import { PrivacyPolicyEn } from './PrivacyPolicyEn';
import { PrivacyPolicyPt } from './PrivacyPolicyPt';
import { TermsEn } from './TermsEn';
import { TermsPt } from './TermsPt';

/**
 * Immutable published document versions (25 September 2026 revision).
 *
 * The version string is part of the acceptance contract: a version recorded
 * against a Terms acceptance must keep pointing at exactly the document that
 * was shown at that moment, even after a later revision is published.
 * The backend mirrors this canonical version (see the endorsement/acceptance
 * work) — never reword an existing version, publish a new one instead.
 */
export const TERMS_DOCUMENT_VERSION = 'terms-2026-09-25';
export const PRIVACY_DOCUMENT_VERSION = 'privacy-2026-09-25';

/** Publication date of the revision above, as an ISO date for `<time dateTime>`. */
export const LEGAL_DOCUMENT_PUBLISHED_ON = '2026-09-25';

export type LegalDocumentKind = 'terms' | 'privacy';

/** Language a document is actually published in. */
export type LegalLocale = 'en' | 'pt';

/** Interface languages the app ships. */
export type SupportedUiLocale = 'en-US' | 'pt-PT' | 'es-ES';

/**
 * Spanish visitors read the English documents until approved Spanish
 * translations exist. This is the documented fallback, never a blank page.
 */
const LEGAL_LOCALE_BY_UI_LOCALE: Record<SupportedUiLocale, LegalLocale> = {
  'en-US': 'en',
  'pt-PT': 'pt',
  'es-ES': 'en',
};

/**
 * Resolve the interface language to one of the languages the app ships,
 * deterministically. Region variants (`pt-BR`, `en-GB`) collapse onto the
 * closest supported language; anything unknown falls back to English.
 */
export const normalizeUiLocale = (language?: string | null): SupportedUiLocale => {
  const normalized = (language ?? '').toLowerCase();

  if (normalized.startsWith('pt')) return 'pt-PT';
  if (normalized.startsWith('es')) return 'es-ES';

  return 'en-US';
};

export const legalLocaleForUiLocale = (uiLocale: SupportedUiLocale): LegalLocale =>
  LEGAL_LOCALE_BY_UI_LOCALE[uiLocale];

/** True when the visitor reads an English document as a fallback for their language. */
export const usesEnglishLegalFallback = (uiLocale: SupportedUiLocale): boolean => uiLocale === 'es-ES';

/** Canonical, locale-prefixed URLs for the published documents. */
export const LEGAL_DOCUMENT_PATHS: Record<LegalLocale, Record<LegalDocumentKind, string>> = {
  pt: {
    terms: '/pt/termos',
    privacy: '/pt/privacidade',
  },
  en: {
    terms: '/en/terms',
    privacy: '/en/privacy',
  },
};

export const legalDocumentPath = (kind: LegalDocumentKind, legalLocale: LegalLocale): string =>
  LEGAL_DOCUMENT_PATHS[legalLocale][kind];

export const legalDocumentPathForUiLocale = (
  kind: LegalDocumentKind,
  uiLocale: SupportedUiLocale,
): string => legalDocumentPath(kind, legalLocaleForUiLocale(uiLocale));

/**
 * The published document a canonical, locale-prefixed path points at, or `null`
 * for any other path. Used to keep an interface-language change on the same
 * document (see LanguageSwitcher). Bare aliases (`/terms`, `/termos`, ...) are
 * deliberately excluded — they redirect on load and are never a stable location.
 */
export const legalDocumentFromPath = (pathname: string): LegalDocumentKind | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  const kinds: LegalDocumentKind[] = ['terms', 'privacy'];
  const locales = Object.keys(LEGAL_DOCUMENT_PATHS) as LegalLocale[];

  for (const kind of kinds) {
    for (const locale of locales) {
      if (LEGAL_DOCUMENT_PATHS[locale][kind] === path) {
        return kind;
      }
    }
  }

  return null;
};

export type LegalDocumentComponent = ComponentType;

const LEGAL_DOCUMENT_TITLES: Record<LegalDocumentKind, Record<LegalLocale, string>> = {
  terms: {
    pt: 'Termos de Serviço',
    en: 'Terms of Service',
  },
  privacy: {
    pt: 'Política de Privacidade',
    en: 'Privacy Policy',
  },
};

/** Document name in the language the document is published in. */
export const legalDocumentTitle = (kind: LegalDocumentKind, legalLocale: LegalLocale): string =>
  LEGAL_DOCUMENT_TITLES[kind][legalLocale];

const LEGAL_DOCUMENT_COMPONENTS: Record<LegalDocumentKind, Record<LegalLocale, LegalDocumentComponent>> = {
  terms: {
    pt: TermsPt,
    en: TermsEn,
  },
  privacy: {
    pt: PrivacyPolicyPt,
    en: PrivacyPolicyEn,
  },
};

export const getLegalDocumentComponent = (
  kind: LegalDocumentKind,
  legalLocale: LegalLocale,
): LegalDocumentComponent => LEGAL_DOCUMENT_COMPONENTS[kind][legalLocale];

/** The document published alongside a given document, in the same language. */
export const counterpartDocumentKind = (kind: LegalDocumentKind): LegalDocumentKind =>
  kind === 'terms' ? 'privacy' : 'terms';

/**
 * i18n key for a document's interface label (footer nav, related-document link).
 * The strings live in `locales/*.json` under `legal.*`; the document bodies
 * themselves stay as static components, because they are published content in a
 * fixed language rather than translated interface copy.
 */
const LEGAL_LABEL_KEYS: Record<LegalDocumentKind, string> = {
  terms: 'legal.terms',
  privacy: 'legal.privacy',
};

export const legalLabelKey = (kind: LegalDocumentKind): string => LEGAL_LABEL_KEYS[kind];
